import { type DocNode, createSchema } from "@atlaskit/adf-schema";
import { defaultSchemaConfig } from "@atlaskit/adf-schema/schema-default";
import type { Transformer } from "@atlaskit/editor-common/types";
import { findFilepaths, isLinkInMatches } from "@atlaskit/editor-common/utils";
import type { Schema, NodeSpec } from "@atlaskit/editor-prosemirror/model";
import { ADFEncoder } from "@atlaskit/renderer/utils";
import { AiMateMarkdownTransformer } from "@atlassian/conversation-assistant-content-transformer";

import AiSummaryAdfEncoder from "./ai-summary-adf-encoder";

export type Content = DocNode | string;

// Button node spec - inline atom node with attributes
const buttonNodeSpec: NodeSpec = {
    group: "inline",
    inline: true,
    atom: true,
    attrs: {
        text: { default: "" },
        type: { default: "" },
        href: { default: "" }
    },
    parseDOM: [
        {
            tag: "span[data-button]",
            getAttrs: (dom) => {
                const el = dom as HTMLElement;
                return {
                    text: el.getAttribute("data-text") || "",
                    type: el.getAttribute("data-type") || "",
                    href: el.getAttribute("data-href") || ""
                };
            }
        }
    ],
    toDOM: (node) => [
        "span",
        {
            "data-button": "",
            "data-text": node.attrs["text"] as string,
            "data-type": node.attrs["type"] as string,
            "data-href": node.attrs["href"] as string
        }
    ]
};

// Extend the default schema with custom node specs
const customSchema = createSchema({
    ...defaultSchemaConfig,
    customNodeSpecs: {
        ...defaultSchemaConfig.customNodeSpecs,
        button: buttonNodeSpec
    }
});

// Regex to match :button[text]{key="value" ...} syntax
const BUTTON_DIRECTIVE_REGEX = /:button\[([^\]]*)\]\{([^}]*)\}/g;

// Parse attributes from {key="value" key2="value2"} format
function parseDirectiveAttrs(attrsString: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    const attrRegex = /(\w+)="([^"]*)"/g;
    let match;
    while ((match = attrRegex.exec(attrsString)) !== null) {
        const key = match[1];
        const value = match[2];
        if (key !== undefined) {
            attrs[key] = value ?? "";
        }
    }
    return attrs;
}

// ADF node types
type AdfNode = {
    type: string;
    attrs?: Record<string, unknown>;
    content?: AdfNode[];
    text?: string;
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
};

// Process a single text node, splitting it if it contains button directives
// Returns null if no changes were made, allowing parent to preserve references
function processTextNode(textNode: AdfNode): AdfNode[] | null {
    if (!textNode.text) return null;

    // Quick check: if no button directive exists, return null to indicate no changes
    BUTTON_DIRECTIVE_REGEX.lastIndex = 0;
    if (!BUTTON_DIRECTIVE_REGEX.test(textNode.text)) {
        return null;
    }

    const result: AdfNode[] = [];
    let lastIndex = 0;
    let match;

    BUTTON_DIRECTIVE_REGEX.lastIndex = 0;

    while ((match = BUTTON_DIRECTIVE_REGEX.exec(textNode.text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            const node: AdfNode = {
                type: "text",
                text: textNode.text.slice(lastIndex, match.index)
            };
            if (textNode.marks) {
                node.marks = textNode.marks;
            }
            result.push(node);
        }

        // Parse the directive
        const buttonText = match[1] ?? "";
        const attrsString = match[2] ?? "";
        const attrs = parseDirectiveAttrs(attrsString);

        // Use inlineExtension - the proper ADF mechanism for custom inline content
        result.push({
            type: "inlineExtension",
            attrs: {
                extensionType: "com.atlassian.wac",
                extensionKey: "cta-button",
                parameters: {
                    text: buttonText,
                    buttonType: attrs["type"] ?? "",
                    href: attrs["href"] ?? ""
                }
            }
        });

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < textNode.text.length) {
        const node: AdfNode = {
            type: "text",
            text: textNode.text.slice(lastIndex)
        };
        if (textNode.marks) {
            node.marks = textNode.marks;
        }
        result.push(node);
    }

    return result;
}

// Recursively process ADF nodes to find and replace button directives
// Returns the original node reference if no changes were made
function processAdfNode(node: AdfNode): AdfNode {
    if (node.type === "text") {
        // Text nodes are handled by the parent's content processing
        return node;
    }

    if (!node.content) {
        return node;
    }

    // Process content array, expanding text nodes that contain directives
    // Track whether any changes were made to preserve references
    let hasChanges = false;
    const newContent: AdfNode[] = [];

    for (const child of node.content) {
        if (child.type === "text") {
            const processed = processTextNode(child);
            if (processed !== null) {
                hasChanges = true;
                newContent.push(...processed);
            } else {
                newContent.push(child);
            }
        } else {
            const processed = processAdfNode(child);
            if (processed !== child) {
                hasChanges = true;
            }
            newContent.push(processed);
        }
    }

    // Return original node if nothing changed
    if (!hasChanges) {
        return node;
    }

    return {
        ...node,
        content: newContent
    };
}

// Post-process ADF to convert :button[text]{attrs} to button nodes
function processButtonDirectives(doc: DocNode): DocNode {
    return processAdfNode(doc as AdfNode) as DocNode;
}

function trimTextNodeIfBeforeStatus(
    nodes: AdfNode[],
    idx: number
): AdfNode | null {
    const current = nodes[idx];
    const previous = nodes[idx - 1];
    if (
        current?.type !== "status" ||
        previous?.type !== "text" ||
        typeof previous.text !== "string"
    ) {
        return null;
    }
    const trimmed = previous.text.replace(/\s+$/u, "");
    return trimmed !== previous.text ? { ...previous, text: trimmed } : null;
}

function processChildNode(
    nodes: AdfNode[],
    idx: number
): { node: AdfNode; changed: boolean } {
    const child = nodes[idx];
    if (!child?.content) {
        return { node: child ?? { type: "text" }, changed: false };
    }
    const processed = trimWhitespaceBeforeStatusNodes(child);
    return { node: processed, changed: processed !== child };
}

// Recursively trims trailing whitespace from text nodes that directly precede
// status nodes so citation markers don't inherit end-of-sentence gaps.
function trimWhitespaceBeforeStatusNodes(node: AdfNode): AdfNode {
    if (!node.content || node.content.length === 0) {
        return node;
    }

    let hasChanges = false;
    const newContent = [...node.content];

    for (let i = 0; i < newContent.length; i += 1) {
        if (i > 0) {
            const trimmedPrev = trimTextNodeIfBeforeStatus(newContent, i);
            if (trimmedPrev) {
                hasChanges = true;
                newContent[i - 1] = trimmedPrev;
            }
        }

        const { node: processedChild, changed } = processChildNode(
            newContent,
            i
        );
        if (changed) {
            hasChanges = true;
            newContent[i] = processedChild;
        }
    }

    return hasChanges ? { ...node, content: newContent } : node;
}

function processCitationWhitespace(doc: DocNode): DocNode {
    return trimWhitespaceBeforeStatusNodes(doc as AdfNode) as DocNode;
}

export const isAdfContent = (content: Content): content is DocNode => {
    /*
    This try-catch is a necessary guard rail against broken content to prevent higher level
    error boundaries from displaying the error and breaking the experience.
    */
    try {
        return (
            typeof content !== "string" &&
            "type" in content &&
            content.type === "doc"
        );
    } catch {
        return false;
    }
};

export const appendContent = (
    content: string | DocNode,
    suggestionContent: string
): string | DocNode => {
    if (isAdfContent(content)) {
        // Ensure the content is an array of nodes
        const updatedContent = Array.isArray(content.content)
            ? content.content
            : [content.content];

        // Append the new paragraph node
        updatedContent.push({
            type: "paragraph",
            content: [
                {
                    type: "text",
                    text: suggestionContent
                }
            ]
        });

        // Return the updated DocNode
        return {
            ...content,
            content: updatedContent
        };
    }
    return `${content}\n\n${suggestionContent}`;
};

const pluginConfig = {
    linkify: {
        findFilepaths,
        isLinkInMatches
    },
    smartLinks: {
        regexStorage: globalThis.localStorage
    }
};

// Default schema is https://bitbucket.org/atlassian/adf-schema/src/main/packages/adf-schema/src/schema/default-schema.ts
// AiMateMarkdownTransformer uses prosemirror-model from @atlaskit/editor-prosemirror; ADFEncoder
// uses @atlaskit/renderer's copy. Same defaultSchema at runtime; cast to satisfy duplicate-package types.
export const adfEncoder = new ADFEncoder(() => {
    return new AiSummaryAdfEncoder(
        customSchema as unknown as Schema,
        pluginConfig,
        false
    ) as unknown as Transformer<string>;
});

const streamingAdfEncoder = new ADFEncoder(() => {
    return new AiMateMarkdownTransformer(
        customSchema as unknown as Schema,
        pluginConfig,
        true
    ) as unknown as Transformer<string>;
});

export const encodeAdf = (content: Content, streaming: boolean): DocNode => {
    if (isAdfContent(content)) {
        return processCitationWhitespace(processButtonDirectives(content));
    }

    const encoded = streaming
        ? streamingAdfEncoder.encode(content)
        : adfEncoder.encode(content);

    return processCitationWhitespace(processButtonDirectives(encoded));
};
